import { A11yModule } from '@angular/cdk/a11y';
import { registerLocaleData } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import de from '@angular/common/locales/de';
import en from '@angular/common/locales/en';
import fr from '@angular/common/locales/fr';
import { LOCALE_ID, NgModule, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { provideTranslateService, TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxStripeModule } from 'ngx-stripe';
import { register } from 'swiper/element/bundle';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { ROUTES } from './app.routes';
import { ComponentsModule } from './components/components.module';
import { EopIconModule } from './shared/components/eop-icon/eop-icon.module';
import { DirectivesModule } from './shared/directives/directives.module';
import { MaterialDesignModule } from './shared/modules/material-design/material-design.module';
import { DynamicContentsService } from './shared/services/dynamic-contents.service';
import { GoogleAnalyticsService } from './shared/services/google-analytics/google-analytics.service';
import { LanguageService } from './shared/services/language.service';
import { LoadingIndicatorService } from './shared/services/loading-indicator.service';
import { PageHistoryService } from './shared/services/page-history.service';
import { PersistenceService } from './shared/services/persistence.service';
import { SessionService } from './shared/services/session.service';
import { SidenavService } from './shared/services/sidenav.service';
import { StationApiService } from './shared/services/station-api/station-api.service';

import { DialogComponent } from './shared/components/dialog/dialog.component';
import { ErrorDialogComponent } from './shared/components/error-dialog/error-dialog.component';
import { IconModule } from './shared/components/icon/icon.module';

const getBrowserLang = () => {
  let lang = 'en';
  if (window.navigator && window.navigator.languages?.length > 0) {
    lang = window.navigator.language;
  }
  return lang.split('-')[0];
};

const COMPONENTS = [ErrorDialogComponent, DialogComponent];

@NgModule({
  declarations: [AppComponent, ...COMPONENTS],
  imports: [
    BrowserModule,
    A11yModule,
    RouterModule.forRoot(ROUTES),
    BrowserAnimationsModule,
    MaterialDesignModule,
    EopIconModule,
    IconModule,
    ReactiveFormsModule,
    FormsModule,
    ComponentsModule,
    HttpClientModule,
    NgxStripeModule.forRoot(),
    DirectivesModule,
    TranslateModule,
  ],
  exports: [
    MaterialDesignModule,
    EopIconModule,
    IconModule,
    ReactiveFormsModule,
    FormsModule,
    ...COMPONENTS,
  ],
  providers: [
    DynamicContentsService,
    PersistenceService,
    SessionService,
    LoadingIndicatorService,
    PageHistoryService,
    SidenavService,
    GoogleAnalyticsService,
    LanguageService,
    StationApiService,
    {
      provide: 'googleTagManagerId',
      useValue: environment.googleTagManager,
    },
    { provide: LOCALE_ID, useFactory: registerLocale },
    provideAppInitializer(() => {
      registerLocale();
    }),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: `${environment.bffUrl}/translations/`,
        suffix: '', // default would be '.json'
      }),
    }),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor() {
    register();
  }
}

function registerLocale(): string {
  const deviceLanguage = getBrowserLang();

  switch (deviceLanguage) {
    case 'de':
      registerLocaleData(de);
      break;
    case 'fr':
      registerLocaleData(fr);
      break;
    default:
      registerLocaleData(en);
      return 'en';
  }
  return deviceLanguage;
}
